<template>
    <ion-page>
      <ion-content id="scriptureTab" ref="scriptureContent" :fullscreen="true" class="ion-no-padding" style="scroll-margin-block-start: 100px;">

            <!-- Custom Content Top -->
            <ion-item lines="full" mode="md" class="wysiwyg-container">
                <ion-label position="stacked" style="overflow: visible;">{{ language.customContent }}</ion-label>
                <MarkdownWYSIWYG v-model="journey.scriptureTabTopContent" v-if="contentLoaded" :placeholder="language.abideByCopyrightLawsPlaceholder"/>
            </ion-item>
            <AudioLibraryPicker v-if="contentLoaded" v-model:audioId="journey.scriptureTabTopAudioId" v-model:audioName="journey.scriptureTabTopAudioFilename" v-model:audioSizeKb="journey.scriptureTabTopAudioSizeKb" :label="language.contentAudioFile" audioGroupId="4a110c9c-84ed-44d4-b3ef-d915ff998f77" />


            <!-- Video -->
            <VideoPicker v-if="contentLoaded" :label="language.journeyVideo1Url" v-model:videoUrl="journey.videoUrl" v-model:videoLabel="journey.videoLabel" />

            <!-- Template Text Top Preview -->
            <TemplateItemPlaceholder  :previewText="templateTab.text1 ?? ''"  id="template-text1"/>
            
            <!-- Scripture -->
            <ScripturePicker v-if="contentLoaded" @onRemove="handleRemoveScriptureRef" @onAdd="handleAddScriptureRef" :appInstanceId="journeySet.appInstanceId" :journeyId="journey.id" :tab=2 :scriptureReferences="tabSpecificScriptureReferences" />
            <AudioLibraryPicker v-if="contentLoaded" v-model:audioId="journey.scriptureAudioId" v-model:audioName="journey.scriptureAudioFilename" v-model:audioSizeKb="journey.scriptureAudioSizeKb" :label="language.scriptureAudioFile" audioGroupId="4a110c9c-84ed-44d4-b3ef-d915ff998f77" />

            <!-- Lesson Text -->
            <ion-item lines="full" mode="md" class="wysiwyg-container">
                <ion-label position="stacked" style="overflow: visible;">{{ language.contentTextStoryMode }}</ion-label>
                <MarkdownWYSIWYG v-model="journey.storyContent" v-if="contentLoaded" :placeholder="language.abideByCopyrightLawsPlaceholder"/>
            </ion-item>
            <AudioLibraryPicker v-if="contentLoaded" v-model:audioId="journey.scriptureTabStoryAudioId" v-model:audioName="journey.scriptureTabStoryAudioFilename" v-model:audioSizeKb="journey.scriptureTabStoryAudioSizeKb" :label="language.contentAudioFile" audioGroupId="4a110c9c-84ed-44d4-b3ef-d915ff998f77" />

            <!-- Template Text Bottom Preview -->
            <TemplateItemPlaceholder  :previewText="templateTab.text2 ?? ''"  id="template-text2"/>

          <ResourcePicker v-if="contentLoaded" v-model:heading="journey.scriptureTabResourceListHeading" :journey-id="journeyId" tab-num="2"/>

        </ion-content>
        <VisualTabs :currentTab="1" :tabs="template.tabs" :activeTabIndexes="activeTabs" v-if="template.tabs"/>
        <FixedFooter :buttons="footerButtons"/>
    </ion-page>
</template>

<script lang="ts">

import {
    IonContent,
    IonItem,
    IonLabel,
    IonIcon,
    IonPage,
    loadingController
} from '@ionic/vue';
import {computed, defineComponent, ref} from 'vue';
import FixedFooter from "@/components/FixedFooter.vue";
import {FooterButtonsBuilder} from "@/mixins/FooterButtonsBuilder";
import {execute, executeWithoutSpinner} from "@/mixins/LoadingMixin";
import {getJourneySet} from "@/services/JourneySetService";
import {getTemplate} from "@/services/TemplateService";
import AudioLibraryPicker from '@/components/AudioLibraryPicker.vue';
import VisualTabs from "@/components/VisualTabs.vue";
import {JourneyTemplate} from "@/models/JourneyTemplateModel";
import {JourneySet} from '@/models/JourneySetModel';
import {Journey, validateJourney, clearLegacyInlineAudioWhenLibraryIdSet, clearLegacyAudioWhenCleared} from "@/models/JourneyModel";
import {getJourney, updateJourney} from "@/services/JourneyService";
import router from "@/router";
import { closeOutline, chevronDownOutline, chevronUpOutline, checkmarkOutline } from 'ionicons/icons';
import {ScriptureSelection} from "@/models/ScriptureSelectionModel";
import {useStore} from "vuex";
import MarkdownWYSIWYG from "@/components/MarkdownWYSIWYG.vue";
import ScripturePicker from '@/components/ScripturePicker.vue';
import VideoPicker from '@/components/Video/VideoPicker.vue';
import { onBeforeRouteUpdate } from 'vue-router';
import TemplateItemPlaceholder from '@/components/TemplateItemPlaceholder.vue';
import { JourneyTab } from '@/models/JourneyTabModel';
import ResourcePicker from '@/components/ResourcePicker.vue';

export default defineComponent({
    name: 'ScriptureContent',
    components: {
        VisualTabs,
        IonContent,
        IonPage,
        IonItem,
        IonLabel,
        AudioLibraryPicker,
        FixedFooter,
        MarkdownWYSIWYG,
        VideoPicker,
        ScripturePicker,
        TemplateItemPlaceholder,
        ResourcePicker
    },
    props: {
        journeySetId: {
            type: String,
            required: true
        },
        journeyId: {
            type: String,
            required: true
        }
    },
    async ionViewWillLeave() {
        await this.saveProgress();
    },
    async ionViewWillEnter() {
        this.contentLoaded = false;
        await this.store.dispatch('app/setPageTitle', this.language.journeyTab2);
        await execute(async () => {
            this.journeySet = await getJourneySet(this.journeySetId);
            this.journey = await getJourney(this.journeyId);
            this.journey = validateJourney(this.journey);
            this.selectedScriptureReferences = this.journey.scriptureSelections;
            this.template = await getTemplate(this.journeySet.templateId);
            if (this.template.enableCustomContent == undefined) this.template.enableCustomContent = false;
            this.contentLoaded = true;
            this.templateTab = this.template.tabs[1];
        }, this.language.errors.loadingJourney);
        this.scriptureContent.$el.scrollToTop(300);
    },
    setup(props) {
        const store = useStore();
        const language = computed(() => store.getters['app/language']);
        const contentLoaded = ref(false);
        const template = ref({} as JourneyTemplate);
        const journeySet = ref({} as JourneySet);
        const journey = ref({} as Journey);
        const scriptureContent = ref();
        const hasFurtherEditing = computed(() => template.value.tabsCount === 4 || template.value.enableCustomContent);
        const nextText = computed(() => (hasFurtherEditing.value || template.value.enableCustomContent) ? language.value.next : language.value.finishAndSave);
        const selectedScriptureReferences = ref([] as ScriptureSelection[]);
        const tabSpecificScriptureReferences = computed(() => selectedScriptureReferences.value.filter((reference) => reference.tabNum == 2))
        const templateTab = ref({} as JourneyTab);
        const activeTabs = computed(() => {
            const result: number[] = [];
            // if (hasFurtherEditing.value) result.push(2);
            // if (template.value.enableCustomContent) {
            //     result.push(0);
            //     result.push(hasFurtherEditing.value ? 3 : 2);
            // }
            switch (template.value.tabsCount) {
                case 1:
                    result.push(3);
                    break;
                case 2:
                    result.push(0);
                    result.push(3);
                    break;
                case 3:
                    if(template.value.enableCustomContent){
                        result.push(0);
                        result.push(1);
                        result.push(3);
                    }else{
                        result.push(1);
                    }
                    break;
                case 4:
                    if(template.value.enableCustomContent){
                        result.push(0);
                        result.push(1);
                        result.push(2);
                        result.push(3);
                    }else{
                        result.push(1);
                        result.push(2);
                    }
                    break;
                }
                return result
        })

        const hasVideo = computed(() => journey.value.videoLabel || journey.value.videoUrl);
        
        const save = async () => {
            await executeWithoutSpinner(async() => {
                journey.value.scriptureSelections = selectedScriptureReferences.value;
                clearLegacyInlineAudioWhenLibraryIdSet(journey.value);
                clearLegacyAudioWhenCleared(journey.value);
                await updateJourney(journey.value);
            }, language.value.errors.savingJourney);
        }

        const saveProgress = async () => {
            const loading = await loadingController.create({
                message: language.value.savingProgress,
            });
            await loading.present();
            await save();
            await loading.dismiss();
        }
        
        const saveAndNext = async () => {
            await saveProgress();
            if (hasFurtherEditing.value && template.value.tabsCount === 4) {
                await router.push({
                    name: 'extraContent',
                    params: props });
            } else if (template.value.enableCustomContent) {
                await router.push({
                    name: 'finalContent',
                    params: props })
            } else {
                await router.push({
                    name: 'journeySetJourneys',
                    params: {journeySetId: props.journeySetId}
                });
            }
        }

        const handleAddScriptureRef = (newRef: ScriptureSelection) => {
            selectedScriptureReferences.value.push(newRef);
        }

        const handleRemoveScriptureRef = (id: string) => {
            const index = selectedScriptureReferences.value.findIndex((reference) => reference.id == id);
            selectedScriptureReferences.value.splice(index, 1);
        }

        onBeforeRouteUpdate(async (to, from, next) => {
            //scriptureContent.value.$el.scrollToTop(300);
        });

        return {
            store,
            language,
            saveProgress,
            save,
            template,
            journeySet,
            journey,
            scriptureContent,
            closeOutline,
            chevronDownOutline,
            chevronUpOutline,
            checkmarkOutline,
            selectedScriptureReferences,
            tabSpecificScriptureReferences,
            contentLoaded,
            hasVideo,
            activeTabs,
            handleAddScriptureRef,
            handleRemoveScriptureRef,
            footerButtons: computed(() => new FooterButtonsBuilder()
                .addClearBackButton()
                .addPrimaryButton( hasFurtherEditing.value, async () => await saveAndNext(), nextText.value).create()),
            templateTab,
        }
    }
});
</script>

<style scoped>

.custom-button {
    margin-bottom: 20px;
}

.form-section-header {
    padding: 20px 40px;
    color: var(--color-step-600);
    text-align: center;
    display: flex;
    justify-content: center;
    flex-direction: column;
}

</style>

<style lang="css" src="../../components/Video/VideoStyling.css"></style>
